import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, Home, Search, X } from 'lucide-react';
import {
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { isSpacerMapNode } from '@/components/CourseMap/course-utils';
import type { CourseNodeBase, CourseNodeEntity } from '@/components/CourseMap/coursemap.types';
import Loader from '@/components/Loader';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
} from '@/components/ui/drawer';
import GlobalContext from '@/context/GlobalContext';
import { getCourseMap } from '@/hooks/queries/useCourseMap';
import { useGetMapCourse } from '@/hooks/queries/useMapCourse';
import { useTabLabels } from '@/hooks/useTabLabels';
import { getFormattedDate } from '@/utils';

import { StyledPanel } from './ParentMapStyles';
import DescriptionTab from './sub-components/DescriptionTab';
import OutcomeTab from './sub-components/OutcomeTab';
import { Panel, Tabs } from './sub-components/ParentMapTabs';
import ResourcesTab from './sub-components/ResourcesTab';
import UnitFocusTab from './sub-components/UnitFocusTab';

// Recursively collect all non-discipline, non-spacer, non-archived course nodes (counts & search)
function flattenCourses(nodes: CourseNodeBase[]): CourseNodeEntity[] {
	return (nodes as CourseNodeEntity[]).flatMap((node) => {
		if (node.isArchived) return [];
		if (isSpacerMapNode(node.name)) {
			// Transparent — don't include the spacer itself, but recurse into its children
			return node.children?.length ? flattenCourses(node.children) : [];
		}
		const self: CourseNodeEntity[] = node.isDiscipline ? [] : [node];
		const nested = node.children?.length ? flattenCourses(node.children) : [];
		return [...self, ...nested];
	});
}

function getVisibleChildren(node: CourseNodeEntity): CourseNodeEntity[] {
	return ((node.children ?? []) as CourseNodeEntity[]).flatMap((c) => {
		if (c.isArchived) return [];
		// Spacers are transparent — skip the node itself, surface its children instead
		if (isSpacerMapNode(c.name)) return getVisibleChildren(c);
		return [c];
	});
}

function getDivisionAbbr(division: string | undefined): string {
	if (!division) return '';
	return `${division.charAt(0).toUpperCase()}S`;
}

// ── Single course row ──────────────────────────────────────────────────────

interface CourseRowProps {
	node: CourseNodeEntity;
	disciplineColor: string;
	onEnter: (node: CourseNodeEntity) => void;
	onView: (tracker: string) => void;
}

function CourseRow({ node, disciplineColor, onEnter, onView }: CourseRowProps) {
	const children = getVisibleChildren(node);
	const hasChildren = children.length > 0;

	const badge = (
		<div
			aria-hidden
			className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-white text-xs font-bold select-none"
			style={{ backgroundColor: disciplineColor }}
		>
			{getDivisionAbbr(node.division)}
		</div>
	);

	const nameBlock = (
		<div className="flex-1 min-w-0 text-left">
			<span className="block text-sm font-medium text-gray-900 leading-snug truncate">
				{node.name}
			</span>
			{hasChildren && (
				<span className="block text-xs text-muted-foreground mt-0.5">
					{children.length} {children.length === 1 ? 'next course' : 'next courses'} →
				</span>
			)}
		</div>
	);

	return (
		<div className="mobile-course-row flex items-center gap-3 px-4 py-3.5 transition-colors">
			{hasChildren ? (
				<button
					type="button"
					aria-label={`Explore ${node.name} pathway`}
					className="flex flex-1 items-center gap-3 min-w-0 bg-transparent border-0 p-0 cursor-pointer active:opacity-70 transition-opacity"
					onClick={() => onEnter(node)}
				>
					{badge}
					{nameBlock}
					<ChevronRight size={16} className="text-muted-foreground shrink-0 mx-1" />
				</button>
			) : (
				<div className="flex flex-1 items-center gap-3 min-w-0">
					{badge}
					{nameBlock}
				</div>
			)}

			{/* View button — separate action from row navigation */}
			<button
				type="button"
				className="shrink-0 px-3 py-1.5 rounded-md text-xs font-semibold text-white transition-opacity active:opacity-75"
				style={{ backgroundColor: 'var(--org-color)' }}
				onClick={(e) => {
					e.stopPropagation();
					onView(node.tracker);
				}}
			>
				View
			</button>
		</div>
	);
}

// ── Main component ─────────────────────────────────────────────────────────

const MobileParentMap = () => {
	const { disciplines: contextDisciplines } = useContext(GlobalContext);
	const labels = useTabLabels();

	/**
	 * navStack drives the push-navigation model.
	 * - Empty   → discipline list screen
	 * - [0]     → discipline node  → shows its direct course children
	 * - [0,1]   → [0] + a course   → shows that course's children
	 * - [0,1,2] → deeper           → shows [2]'s children, etc.
	 */
	const [navStack, setNavStack] = useState<CourseNodeEntity[]>([]);
	const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');

	const containerRef = useRef<HTMLDivElement>(null);
	const [topOffset, setTopOffset] = useState(92);

	const { data: treeData, isLoading: treeLoading } = useQuery({
		queryKey: ['get-course-map', import.meta.env.VITE_SCHOOL_ID],
		queryFn: getCourseMap,
		staleTime: Infinity,
		refetchOnMount: 'always',
	});

	useLayoutEffect(() => {
		const update = () => {
			if (containerRef.current) {
				setTopOffset(containerRef.current.offsetTop);
			}
		};
		update();
		window.addEventListener('resize', update);
		return () => window.removeEventListener('resize', update);
	}, [treeLoading]);

	const { data: courseData, isLoading: courseLoading } = useGetMapCourse(selectedCourseId);

	const disciplineNodes = useMemo(
		() =>
			((treeData?.children ?? []) as CourseNodeEntity[]).filter(
				(n) => n.isDiscipline && !n.isArchived
			),
		[treeData]
	);

	// The node whose children are currently displayed (top of the stack)
	const currentParent = navStack.length > 0 ? navStack[navStack.length - 1] : null;

	// Direct visible children of the current parent node
	const currentNodes = useMemo(
		() => (currentParent ? getVisibleChildren(currentParent) : []),
		[currentParent]
	);

	// Discipline node is always navStack[0] when we're in a pathway
	const disciplineNode = navStack.length > 0 ? navStack[0] : null;

	// Breadcrumb scroll container — auto-scrolls to the newest chip on every push
	const breadcrumbRef = useRef<HTMLDivElement>(null);
	// Subway map scroll container — also auto-scrolls to the newest node
	const subwayRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (breadcrumbRef.current) {
			breadcrumbRef.current.scrollLeft = breadcrumbRef.current.scrollWidth;
		}
		if (subwayRef.current) {
			subwayRef.current.scrollLeft = subwayRef.current.scrollWidth;
		}
	}, [navStack.length]);

	// Total unique course count for the discipline (header badge)
	const totalCourseCount = useMemo(() => {
		if (!disciplineNode) return 0;
		const seen = new Set<string>();
		return flattenCourses(disciplineNode.children ?? []).filter((c) => {
			if (seen.has(c.tracker)) return false;
			seen.add(c.tracker);
			return true;
		}).length;
	}, [disciplineNode]);

	// Cross-discipline search
	const searchResults = useMemo(() => {
		const q = searchQuery.trim().toLowerCase();
		if (!q) return [];
		const seen = new Set<string>();
		return disciplineNodes
			.flatMap((discipline) =>
				flattenCourses(discipline.children ?? [])
					.filter((c) => c.name?.toLowerCase().includes(q))
					.map((c) => ({ course: c, discipline }))
			)
			.filter(({ course }) => {
				if (seen.has(course.tracker)) return false;
				seen.add(course.tracker);
				return true;
			});
	}, [searchQuery, disciplineNodes]);

	const handleViewCourse = useCallback((courseId: string) => {
		setSelectedCourseId(courseId);
		setIsDrawerOpen(true);
	}, []);

	// Push a discipline onto the stack (from discipline list)
	const enterDiscipline = useCallback((discipline: CourseNodeEntity) => {
		setNavStack([discipline]);
		setSearchQuery('');
	}, []);

	// Push a course onto the stack (from within the pathway)
	const enterCourse = useCallback((course: CourseNodeEntity) => {
		setNavStack((prev) => [...prev, course]);
	}, []);

	// Pop one level — if stack hits empty, returns to discipline list
	const goBack = useCallback(() => {
		setNavStack((prev) => prev.slice(0, -1));
	}, []);

	// Reset to the discipline list from anywhere in the stack
	const goHome = useCallback(() => {
		setNavStack([]);
	}, []);

	// Jump directly to any point in the trail by index
	const jumpToIndex = useCallback((index: number) => {
		setNavStack((prev) => prev.slice(0, index + 1));
	}, []);

	return (
		<div
			ref={containerRef}
			className="flex flex-col overflow-hidden bg-white"
			style={{ height: `calc(100dvh - ${topOffset}px)` }}
		>
			{treeLoading ? (
				<div className="flex flex-1 items-center justify-center">
					<Loader color="var(--org-color)" ringColor="var(--org-color)" />
				</div>
			) : currentParent ? (
				/* ── Pathway screen ─────────────────────────────────────────────── */
				<div className="flex flex-col h-full">
					{/* Header */}
					<div
						className="shrink-0 border-b border-border bg-white"
						style={{ borderLeft: `4px solid ${disciplineNode?.color ?? 'var(--org-color)'}` }}
					>
						{/* Title row */}
						<div className="flex items-center gap-3 px-4 pt-3 pb-1">
							<button
								type="button"
								aria-label="Go back"
								onClick={goBack}
								className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-black/8 transition-colors shrink-0"
							>
								<ArrowLeft size={18} className="text-gray-600" />
							</button>
							<h2 className="flex-1 text-base font-semibold text-gray-900 truncate">
								{currentParent.name}
							</h2>
							{navStack.length === 1 && (
								<span className="text-xs text-muted-foreground shrink-0">
									{totalCourseCount} courses
								</span>
							)}
						</div>

						{/* Scrollable path trail — home icon anchors the left of the breadcrumb */}
						<div className="flex items-center gap-1 px-3 pb-2.5 pt-0.5">
							<button
								type="button"
								aria-label="Back to disciplines"
								onClick={goHome}
								className="mobile-home-btn flex items-center justify-center shrink-0 text-muted-foreground hover:text-foreground transition-colors"
							>
								<Home size={13} />
							</button>
							{/* biome-ignore lint: scroll-only gesture container */}
							<div
								ref={breadcrumbRef}
								className="mobile-breadcrumbs flex items-center gap-0 overflow-x-auto"
								style={
									{
										scrollbarWidth: 'none',
										WebkitOverflowScrolling: 'touch',
									} as React.CSSProperties
								}
							>
								{navStack.map((node, index) => {
									const isLast = index === navStack.length - 1;
									const chipColor = disciplineNode?.color ?? 'var(--org-color)';
									return (
										<span key={node.tracker} className="flex items-center shrink-0">
											<ChevronRight size={12} className="text-muted-foreground/50 shrink-0" />
											<button
												type="button"
												onClick={() => jumpToIndex(index)}
												disabled={isLast}
												className="px-2 py-0.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap"
												style={
													isLast
														? {
																backgroundColor: `${chipColor}20`,
																cursor: 'default',
															}
														: {
																backgroundColor: 'transparent',
																color: 'var(--muted-foreground)',
															}
												}
											>
												{node.name}
											</button>
										</span>
									);
								})}
							</div>
						</div>
					</div>

					{/* Course list */}
					<div className="flex-1 overflow-y-auto divide-y divide-border">
						{currentNodes.length === 0 ? (
							<p className="p-8 text-center text-sm text-muted-foreground">No courses available.</p>
						) : (
							currentNodes.map((node) => (
								<CourseRow
									key={node.tracker}
									node={node}
									disciplineColor={disciplineNode?.color ?? 'var(--org-color)'}
									onEnter={enterCourse}
									onView={handleViewCourse}
								/>
							))
						)}
					</div>

					{/* ── Subway map strip — visible once at least one course is in the path ── */}
					{navStack.length > 1 &&
						(() => {
							const color = disciplineNode?.color ?? 'var(--org-color)';
							// Course nodes only (skip navStack[0] which is the discipline)
							const pathNodes = navStack.slice(1);
							return (
								<div className="mobile-subway-strip shrink-0 border-t border-border bg-gray-50/60 py-3">
									<div className="flex items-center justify-between px-4 mb-2">
										<span className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
											Your path
										</span>
										<span className="text-xs text-muted-foreground">Tap a course to view</span>
									</div>
									{/* biome-ignore lint: scroll-only gesture container */}
									<div
										ref={subwayRef}
										className="flex items-start px-4 overflow-x-auto"
										style={
											{
												scrollbarWidth: 'none',
												WebkitOverflowScrolling: 'touch',
											} as React.CSSProperties
										}
									>
										{pathNodes.map((node, i) => {
											const isLast = i === pathNodes.length - 1;
											return (
												<div key={node.tracker} className="flex items-start shrink-0">
													{/* Node: circle + name, tap = view detail */}
													<button
														type="button"
														aria-label={`View ${node.name}`}
														onClick={() => handleViewCourse(node.tracker)}
														className="flex flex-col items-center gap-1.5 active:opacity-70 transition-opacity"
														style={{ width: 64 }}
													>
														<div
															className="w-7 h-7 rounded-full shrink-0"
															style={{
																backgroundColor: isLast ? color : `${color}55`,
																boxShadow: isLast ? `0 0 0 3px ${color}25` : 'none',
															}}
														/>
														<span
															className="text-center leading-tight"
															style={
																{
																	fontSize: 10,
																	fontWeight: isLast ? 600 : 400,
																	maxWidth: 64,
																	display: '-webkit-box',
																	WebkitLineClamp: 2,
																	WebkitBoxOrient: 'vertical',
																	overflow: 'hidden',
																} as React.CSSProperties
															}
														>
															{node.name}
														</span>
													</button>

													{/* Connector line — positioned to align with circle center (28px / 2 − 1px) */}
													{!isLast && (
														<div
															className="shrink-0"
															style={{
																width: 16,
																height: 2,
																marginTop: 13,
																backgroundColor: `${color}40`,
															}}
														/>
													)}
												</div>
											);
										})}
									</div>
								</div>
							);
						})()}
				</div>
			) : (
				/* ── Discipline list screen ─────────────────────────────────────── */
				<div className="flex-1 flex flex-col overflow-hidden">
					{/* Search bar */}
					<div className="mobile-search-bar px-4 pt-4 pb-3 shrink-0">
						<div className="relative">
							<Search
								size={16}
								className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
							/>
							<input
								type="text"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								placeholder="Search courses…"
								className="w-full pl-9 pr-9 py-2 text-sm border border-input rounded-lg bg-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
							/>
							{searchQuery && (
								<button
									type="button"
									onClick={() => setSearchQuery('')}
									aria-label="Clear search"
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
								>
									<X size={14} />
								</button>
							)}
						</div>
					</div>

					{searchQuery ? (
						/* ── Search results ─────────────────────────────── */
						<div className="flex-1 overflow-y-auto divide-y divide-border">
							{searchResults.length === 0 ? (
								<p className="p-8 text-center text-sm text-muted-foreground">
									No courses matching &ldquo;{searchQuery}&rdquo;
								</p>
							) : (
								searchResults.map(({ course, discipline }) => (
									<div key={course.tracker} className="flex items-center gap-3 px-4 py-3.5">
										<div
											aria-hidden
											className="flex items-center justify-center w-9 h-9 rounded-full shrink-0 text-white text-xs font-bold select-none"
											style={{ backgroundColor: discipline.color }}
										>
											{getDivisionAbbr(course.division)}
										</div>
										<div className="flex-1 min-w-0">
											<span className="block text-sm font-medium text-gray-900 leading-snug truncate">
												{course.name}
											</span>
											<span className="block text-xs text-muted-foreground truncate">
												{discipline.name}
											</span>
										</div>
										<button
											type="button"
											className="shrink-0 px-3 py-1.5 rounded-md text-xs font-semibold text-white transition-opacity active:opacity-75"
											style={{ backgroundColor: 'var(--org-color)' }}
											onClick={() => handleViewCourse(course.tracker)}
										>
											View
										</button>
									</div>
								))
							)}
						</div>
					) : (
						/* ── Discipline cards ───────────────────────────── */
						<div className="mobile-discipline-list flex-1 overflow-y-auto">
							<div className="p-4 flex flex-col gap-3">
								{disciplineNodes.map((discipline) => {
									const disciplineImg = contextDisciplines?.find(
										(d) => d.name === discipline.name
									)?.image;
									const courseCount = flattenCourses(discipline.children ?? []).length;

									return (
										<button
											key={discipline.tracker}
											type="button"
											onClick={() => enterDiscipline(discipline)}
											className="mobile-discipline-card relative flex items-center overflow-hidden rounded-xl text-left w-full active:scale-[0.98] transition-transform"
											style={{ minHeight: 80, borderLeft: `5px solid ${discipline.color}` }}
										>
											{disciplineImg && (
												<div
													className="absolute inset-0"
													style={{
														backgroundImage: `url(${disciplineImg})`,
														backgroundSize: 'cover',
														backgroundPosition: 'center',
													}}
												/>
											)}
											<div
												className="absolute inset-0"
												style={{
													backgroundColor: disciplineImg ? 'rgba(0,0,0,0.45)' : discipline.color,
												}}
											/>
											<div className="relative flex items-center gap-2 px-4 py-4 w-full">
												<div className="flex-1">
													<span className="block text-base font-semibold text-white leading-tight">
														{discipline.name}
													</span>
													<span className="block text-xs text-white/75 mt-0.5">
														{courseCount} {courseCount === 1 ? 'course' : 'courses'}
													</span>
												</div>
												<ChevronRight size={20} className="text-white/70 shrink-0" />
											</div>
										</button>
									);
								})}
							</div>
						</div>
					)}
				</div>
			)}

			{/* ── Course detail bottom drawer ──────────────────────────────────── */}
			<Drawer direction="bottom" open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
				<DrawerContent className="h-[85dvh]">
					<DrawerHeader className="shrink-0 border-b pb-3 text-left">
						<DrawerTitle className="text font-semibold leading-snug pr-8">
							{courseLoading ? '\u00A0' : courseData?.course_name}
						</DrawerTitle>
						<DrawerDescription className="sr-only">
							{courseLoading ? '' : `Details for ${courseData?.course_name ?? 'course'}`}
						</DrawerDescription>
						{!courseLoading && courseData?.last_updated && (
							<time className="block text-xs text-muted-foreground italic mt-0.5">
								Last Updated {getFormattedDate(courseData.last_updated)}
							</time>
						)}
						{courseLoading && (
							<div className="flex justify-center pt-2">
								<Loader size="small" color="var(--org-color)" ringColor="var(--org-color)" />
							</div>
						)}
					</DrawerHeader>

					{!courseLoading && courseData && (
						<div key={selectedCourseId} className="flex-1 overflow-hidden relative">
							<StyledPanel
								style={{ position: 'absolute', inset: 0, boxShadow: 'none', width: '100%' }}
							>
								<Tabs selected={0}>
									<Panel title={labels.description}>
										<DescriptionTab
											courseDescription={courseData.course_description ?? ''}
											courseExtras={courseData.course_extras ?? ''}
											coursePrereq={courseData.course_prereq ?? ''}
										/>
									</Panel>
									<Panel title={labels.outcomes}>
										<OutcomeTab courseOutcome={courseData.courses_outcomes} />
									</Panel>
									<Panel title={labels.resources}>
										<ResourcesTab
											courseResource={courseData.courses_resources}
											courseResourceInfo={courseData.course_resource_info ?? ''}
										/>
									</Panel>
									<Panel title={labels.units}>
										<UnitFocusTab courseUnit={courseData.courses_focuses} />
									</Panel>
								</Tabs>
							</StyledPanel>
						</div>
					)}
				</DrawerContent>
			</Drawer>
		</div>
	);
};

export default MobileParentMap;
